import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ReactComponent as LogoMark } from '../assets/images/logo-mark.svg';

const C = { void:'#030A3C', cyan:'#00FFFF', magma:'#FF5722', white:'#F8F9FA', muted:'rgba(248,249,250,0.82)', muted2:'rgba(248,249,250,0.72)', border:'rgba(0,255,255,0.08)', border2:'rgba(0,255,255,0.15)' };

const Logo = () => (
  <Link to="/" style={s.logo}>
    <LogoMark width="22" height="22" style={{flexShrink:0}} />
    SenseCritiq
  </Link>
);

const BurgerIcon = ({ open }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
    {open ? (
      <>
        <line x1="4" y1="4" x2="16" y2="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="16" y1="4" x2="4" y2="16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      </>
    ) : (
      <>
        <line x1="3" y1="6"  x2="17" y2="6"  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="3" y1="10" x2="17" y2="10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        <line x1="3" y1="14" x2="17" y2="14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
      </>
    )}
  </svg>
);

export default function MarketingNav() {
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close menu on route change
  useEffect(() => { setMenuOpen(false); }, [pathname]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const navLink = (to, label) => (
    <Link
      to={to}
      style={{...s.navLink, ...(pathname === to ? s.navLinkActive : {})}}
      onClick={() => setMenuOpen(false)}
    >
      {label}
    </Link>
  );

  const mobileLink = (to, label) => (
    <Link
      to={to}
      style={{...s.mobileLink, ...(pathname === to ? s.mobileLinkActive : {})}}
      onClick={() => setMenuOpen(false)}
    >
      {label}
    </Link>
  );

  return (
    <>
      <nav style={{...s.nav, ...(scrolled ? s.navScrolled : {})}} className="scq-nav">
        <Logo />
        <div style={s.links} className="scq-nav-links">
          {navLink('/', 'Home')}
          {navLink('/how-it-works', 'How it works')}
          {navLink('/features', 'Features')}
          {navLink('/researchers', 'For researchers')}
          {navLink('/pricing', 'Pricing')}
        </div>
        <div style={s.actions}>
          <a href="/#waitlist" style={s.cta} className="scq-nav-cta">Join the waitlist</a>
          <button
            style={s.burger}
            className="scq-burger"
            onClick={() => setMenuOpen(o => !o)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            <BurgerIcon open={menuOpen} />
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      {menuOpen && (
        <div style={s.overlay} onClick={() => setMenuOpen(false)}>
          <div style={s.drawer} onClick={e => e.stopPropagation()}>
            <div style={s.drawerHeader}>
              <Logo />
              <button style={s.drawerClose} onClick={() => setMenuOpen(false)} aria-label="Close menu">
                <BurgerIcon open={true} />
              </button>
            </div>
            <div style={s.drawerLinks}>
              {mobileLink('/', 'Home')}
              {mobileLink('/how-it-works', 'How it works')}
              {mobileLink('/features', 'Features')}
              {mobileLink('/researchers', 'For researchers')}
              {mobileLink('/pricing', 'Pricing')}
            </div>
            <div style={s.drawerActions}>
              <a href="/#waitlist" style={s.drawerCta} onClick={() => setMenuOpen(false)}>Join the waitlist</a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const s = {
  nav: { display:'grid', gridTemplateColumns:'1fr auto 1fr', alignItems:'center', padding:'18px 48px', background:'rgba(3,10,60,0.7)', backdropFilter:'blur(16px)', borderBottom:'1px solid transparent', position:'sticky', top:0, zIndex:200, transition:'background 0.2s, border-color 0.2s' },
  navScrolled: { background:'rgba(3,10,60,0.95)', borderBottom:`1px solid ${C.border}` },
  logo: { fontFamily:"'Satoshi','Inter',sans-serif", fontSize:17, fontWeight:700, letterSpacing:'-0.02em', color:C.white, textDecoration:'none', display:'flex', alignItems:'center', gap:4, flexShrink:0 },
  links: { display:'flex', gap:28, justifyContent:'center' },
  navLink: { fontSize:14, color:C.muted, textDecoration:'none', transition:'color 0.15s' },
  navLinkActive: { color:C.cyan },
  actions: { display:'flex', alignItems:'center', gap:16, justifyContent:'flex-end' },
  signIn: { fontSize:14, color:C.muted, textDecoration:'none' },
  cta: { fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:700, fontSize:13, padding:'10px 20px', borderRadius:8, background:C.magma, color:'#fff', textDecoration:'none' },
  burger: { display:'none', background:'none', border:'none', color:C.white, cursor:'pointer', padding:4, lineHeight:0 },

  // Mobile overlay + drawer
  overlay: { position:'fixed', inset:0, background:'rgba(3,10,60,0.6)', backdropFilter:'blur(4px)', zIndex:300 },
  drawer: { position:'absolute', top:0, right:0, bottom:0, width:280, background:'#060D4A', borderLeft:`1px solid ${C.border2}`, display:'flex', flexDirection:'column', padding:'0 0 32px' },
  drawerHeader: { display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 20px', borderBottom:`1px solid ${C.border}` },
  drawerClose: { background:'none', border:'none', color:C.muted, cursor:'pointer', padding:4, lineHeight:0 },
  drawerLinks: { display:'flex', flexDirection:'column', padding:'24px 20px', gap:4, flex:1 },
  mobileLink: { fontSize:16, color:C.muted, textDecoration:'none', padding:'12px 8px', borderRadius:8, fontFamily:"'Satoshi','Inter',sans-serif", fontWeight:500 },
  mobileLinkActive: { color:C.cyan, background:'rgba(0,255,255,0.06)' },
  drawerActions: { display:'flex', flexDirection:'column', gap:10, padding:'0 20px' },
  drawerSignIn: { display:'block', textAlign:'center', padding:'13px', borderRadius:10, fontSize:15, fontWeight:600, color:C.muted, textDecoration:'none', border:`1px solid ${C.border}`, fontFamily:"'Satoshi','Inter',sans-serif" },
  drawerCta: { display:'block', textAlign:'center', padding:'13px', borderRadius:10, fontSize:15, fontWeight:700, background:C.magma, color:'#fff', textDecoration:'none', fontFamily:"'Satoshi','Inter',sans-serif" },
};
